import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Folder,
  LayoutGrid,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { MENU_SECTION_LABEL } from '@/shared/ui-class';
import { useChat } from '@/versions/v1/shared/ChatContext';

const DEFAULT_BACKDROP_CLASS =
  'fixed inset-0 z-50 flex items-start justify-center bg-[rgba(26,19,15,0.42)] px-4 pt-[18vh] pb-6 backdrop-blur-md';

const DEFAULT_CARD_CLASS =
  'relative flex w-full max-w-[640px] flex-col overflow-hidden rounded-2xl border border-[var(--glass-border-strong)] bg-[var(--glass-surface-strong)] shadow-[0_24px_64px_-12px_rgba(20,24,40,0.28)] backdrop-blur-2xl';

const SEARCH_INPUT_CLASS =
  'w-full bg-transparent px-5 py-4 text-lg font-medium text-[var(--foreground)] placeholder:text-zinc-400 outline-none focus:outline-none';

type StarterRow = { key: string; Icon: LucideIcon; label: string; prompt?: string };

const STARTER_ROWS: StarterRow[] = [
  { key: 'summarise', Icon: Sparkles, label: 'Summarise this project' },
  { key: 'sources', Icon: FileText, label: 'What sources are in context?' },
  { key: 'status', Icon: FileText, label: 'Draft a status update' },
  { key: 'scaffold', Icon: FileText, label: 'Explain this scaffold' },
];

type NavigateRow = { key: string; Icon: LucideIcon; label: string; to: string };

const NAVIGATE_ROWS: NavigateRow[] = [
  { key: 'home', Icon: LayoutGrid, label: 'Home', to: '/' },
  { key: 'alpha', Icon: Folder, label: 'Project Alpha', to: '/projects/alpha' },
  { key: 'docs', Icon: FileText, label: 'Build docs', to: '/docs/logic' },
  { key: 'changelog', Icon: FileText, label: 'Changelog', to: '/changelog' },
];

export function ThreadSpotlight() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { send, openRail } = useChat();
  const navigate = useNavigate();

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener('app:open-palette', openHandler);
    return () => window.removeEventListener('app:open-palette', openHandler);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const active = document.activeElement;
      const isEditable =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLElement && active.isContentEditable);

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        if (isEditable && active !== inputRef.current) return;
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (e.key === 'Escape' && open) {
        if (isEditable && active !== inputRef.current) return;
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      window.setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setDraft('');
    }
  }, [open]);

  const commit = useCallback(
    (text: string) => {
      const message = text.trim();
      if (!message) return;
      send(message);
      openRail();
      setOpen(false);
      setDraft('');
    },
    [send, openRail],
  );

  if (!open) return null;

  const modal = (
    <Commentable name="ThreadSpotlight">
      <div
        className={DEFAULT_BACKDROP_CLASS}
        role="dialog"
        aria-modal="true"
        aria-label="Thread spotlight"
        onClick={(e) => {
          if (e.target === e.currentTarget) setOpen(false);
        }}
      >
        <div className={DEFAULT_CARD_CLASS} onClick={(e) => e.stopPropagation()}>
          <div className="focus-within:ring-2 focus-within:ring-[color-mix(in_srgb,var(--color-accent-500)_20%,transparent)]">
            <input
              ref={inputRef}
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commit(draft);
                }
              }}
              placeholder="What do you want to do?"
              className={SEARCH_INPUT_CLASS}
              aria-label="Spotlight composer"
            />
          </div>
          <StarterList
            title="Starters"
            rows={STARTER_ROWS}
            onSelect={(row) => commit(row.prompt ?? row.label)}
          />
          <div className="flex flex-col gap-px border-t border-[var(--glass-border)] bg-[var(--glass-surface-soft)] px-2 py-2">
            <h2 className={cn(MENU_SECTION_LABEL, 'px-1')}>Navigate</h2>
            {NAVIGATE_ROWS.map((row) => (
              <button
                key={row.key}
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(row.to);
                }}
                className="flex items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-zinc-700 outline-none transition-colors hover:bg-[var(--glass-surface-strong)] hover:text-accent-700 focus:outline-none dark:text-zinc-300"
              >
                <row.Icon className="h-4 w-4 flex-shrink-0 text-zinc-400" />
                <span className="flex-1 truncate font-medium">{row.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </Commentable>
  );

  if (typeof document === 'undefined') return modal;
  return createPortal(modal, document.body);
}

function StarterList<T extends { key: string; Icon: LucideIcon; label: string }>({
  title,
  rows,
  onSelect,
}: {
  title: string;
  rows: T[];
  onSelect: (row: T) => void;
}) {
  return (
    <div className="flex flex-col gap-px border-t border-[var(--glass-border)] bg-[var(--glass-surface-soft)] px-2 py-2">
      <h2 className={cn(MENU_SECTION_LABEL, 'px-1')}>{title}</h2>
      {rows.map((row) => (
        <button
          key={row.key}
          type="button"
          onClick={() => onSelect(row)}
          className="flex items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-zinc-700 outline-none transition-colors hover:bg-[var(--glass-surface-strong)] hover:text-accent-700 focus:outline-none dark:text-zinc-300"
        >
          <row.Icon className="h-4 w-4 flex-shrink-0 text-zinc-400" />
          <span className="flex-1 truncate font-medium">{row.label}</span>
        </button>
      ))}
    </div>
  );
}
